import React, { useState, useEffect, useMemo } from 'react';
import { fmtDateTime } from '@shared/formatDate';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
  Stack,
  TextField,
} from '@mui/material';
import {
  MonetizationOn as EarnIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Campaign as NetworkIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  earnCreditsService,
  openQixitNetworkOffer,
  type AdOfferwallSort,
  type EarnTransaction,
} from '../services/earnCreditsService';
import type { Transaction } from '../types';
import type { PromoTransaction } from '../services/accountService';
import TransactionDetailsDialog from '../components/shared/TransactionDetailsDialog';
import PromoTransactionDetailsDialog from '../components/shared/PromoTransactionDetailsDialog';
import {
  AdEarnImage,
  ACTIVE_OVERLAY_BADGE_LOGO,
  OVERLAY_BADGE_LOGO_2WAY_FALLBACK_SVG,
} from '@shared/ad-network';
import { useAuth } from '../hooks/useAuth';
import { getVisibleTabKeys } from '../services/configService';

const EARN_SUBTABS = [
  { key: 'earn-credits/qix-network', label: 'Qix Network', icon: <NetworkIcon /> },
  { key: 'earn-credits/my-earnings', label: 'My Earnings', icon: <HistoryIcon /> },
  { key: 'earn-credits/earn', label: 'Earn', icon: <EarnIcon /> },
] as const;

type EarnSubTabKey = (typeof EARN_SUBTABS)[number]['key'];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const EarnCredits: React.FC = () => {
  const { user, hasAdvancedMailbox } = useAuth();
  const { data: visibleTabKeys = [] } = useQuery({
    queryKey: ['visibleTabKeys'],
    queryFn: getVisibleTabKeys,
    staleTime: 5 * 60 * 1000,
    enabled: !!user?.username,
  });

  const visibleSubTabs = useMemo(() => {
    const isVisible = (key: string) =>
      hasAdvancedMailbox
      || visibleTabKeys.length === 0
      || visibleTabKeys.includes(key);
    return EARN_SUBTABS.filter((t) => isVisible(t.key));
  }, [hasAdvancedMailbox, visibleTabKeys]);

  const [tabIndex, setTabIndex] = useState(0);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [wallUrl, setWallUrl] = useState<string>('');
  const [wallLoading, setWallLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [offerSort, setOfferSort] = useState<AdOfferwallSort>('payDesc');
  const [offerCategory, setOfferCategory] = useState('all');
  const [offerPaymentType, setOfferPaymentType] = useState('all');
  const [offerMinPay, setOfferMinPay] = useState('');
  const [openingCreativeId, setOpeningCreativeId] = useState<string | null>(null);
  const [offerOpenError, setOfferOpenError] = useState('');
  const [selectedAccountTx, setSelectedAccountTx] = useState<Transaction | null>(null);
  const [accountDetailsOpen, setAccountDetailsOpen] = useState(false);
  const [selectedPromoTx, setSelectedPromoTx] = useState<PromoTransaction | null>(null);
  const [promoDetailsOpen, setPromoDetailsOpen] = useState(false);
  const [historyDetailError, setHistoryDetailError] = useState('');

  useEffect(() => {
    if (visibleSubTabs.length === 0) return;
    if (tabIndex >= visibleSubTabs.length) setTabIndex(0);
  }, [visibleSubTabs, tabIndex]);

  const activeSubTabKey: EarnSubTabKey | null = visibleSubTabs[tabIndex]?.key ?? null;

  // Fetch providers
  const {
    data: providers,
    isLoading: providersLoading,
    error: providersError,
  } = useQuery({
    queryKey: ['earnProviders'],
    queryFn: () => earnCreditsService.getProviders(),
    staleTime: 300000, // 5 minutes
  });

  // Fetch summary
  const {
    data: summary,
    isLoading: summaryLoading,
    refetch: refetchSummary,
  } = useQuery({
    queryKey: ['earnSummary'],
    queryFn: () => earnCreditsService.getSummary(),
    staleTime: 30000,
  });

  // Fetch history
  const {
    data: historyData,
    isLoading: historyLoading,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: ['earnHistory', page, rowsPerPage],
    queryFn: () => earnCreditsService.getHistory(rowsPerPage, page * rowsPerPage),
    staleTime: 30000,
  });

  const minPayParsed = (() => {
    const n = Number(String(offerMinPay).replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : undefined;
  })();

  const {
    data: adOffersData,
    isLoading: adOffersLoading,
    error: adOffersError,
    refetch: refetchAdOffers,
  } = useQuery({
    queryKey: ['earnAdOffers', offerSort, offerCategory, offerPaymentType, minPayParsed],
    queryFn: () =>
      earnCreditsService.listAdOffers({
        sort: offerSort,
        category: offerCategory,
        paymentType: offerPaymentType,
        minPay: minPayParsed,
      }),
    enabled: activeSubTabKey === 'earn-credits/qix-network',
    staleTime: 20000,
  });

  // Auto-select first provider
  useEffect(() => {
    if (providers && providers.length > 0 && !selectedProvider) {
      setSelectedProvider(providers[0].providerCode);
    }
  }, [providers, selectedProvider]);

  // Load wall URL when provider changes
  useEffect(() => {
    if (selectedProvider) {
      setWallLoading(true);
      earnCreditsService
        .getWallUrl(selectedProvider)
        .then((result) => {
          setWallUrl(result.url);
          setWallLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load wall URL:', err);
          setWallUrl('');
          setWallLoading(false);
        });
    }
  }, [selectedProvider]);

  useEffect(() => {
    if (activeSubTabKey !== 'earn-credits/qix-network') return;
    const onFocus = () => { refetchAdOffers(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [activeSubTabKey, refetchAdOffers]);

  const getStatusChip = (status: string) => {
    switch (status.toUpperCase()) {
      case 'CREDITED':
        return <Chip label="Credited" color="success" size="small" />;
      case 'PENDING':
        return <Chip label="Pending" color="warning" size="small" />;
      case 'REVERSED':
        return <Chip label="Reversed" color="error" size="small" />;
      case 'REJECTED':
        return <Chip label="Rejected" color="error" size="small" variant="outlined" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return fmtDateTime(dateStr);
  };

  const handleRefresh = () => {
    refetchSummary();
    refetchHistory();
    if (activeSubTabKey === 'earn-credits/qix-network') refetchAdOffers();
  };

  const handleOpenOffer = async (creativeId: string) => {
    setOfferOpenError('');
    setOpeningCreativeId(creativeId);
    try {
      const open = await earnCreditsService.openAdOffer(creativeId);
      if (!open.paymentFastUrl) {
        setOfferOpenError('Could not open this offer. Please try again.');
        return;
      }
      openQixitNetworkOffer(open);
      window.setTimeout(() => { refetchAdOffers(); }, 2500);
    } catch (e: any) {
      setOfferOpenError(e?.response?.data?.message || e?.message || 'Could not open this offer.');
    } finally {
      setOpeningCreativeId(null);
    }
  };

  const handleEarnRowClick = (tx: EarnTransaction) => {
    setHistoryDetailError('');
    const accountHistoryId = tx.accountHistoryId || (tx as any).AccountHistoryId;
    const promoHistoryId = tx.promoHistoryId || (tx as any).PromoHistoryId;

    if (tx.paymentType === 1 || promoHistoryId) {
      if (!promoHistoryId) {
        setHistoryDetailError('Vendor credit details are not available for this claim yet.');
        return;
      }
      const amount = Number(tx.amountEarned ?? 0);
      const balanceBefore = Number(tx.promoBalanceBefore ?? (tx as any).PromoBalanceBefore ?? 0);
      const balanceAfter = Number(
        tx.promoBalanceAfter ?? (tx as any).PromoBalanceAfter ?? balanceBefore + amount
      );
      setSelectedPromoTx({
        transactionId: String(promoHistoryId),
        promoCreditId: String(tx.promoCreditId || (tx as any).PromoCreditId || ''),
        sponsorUsername: String(tx.promoSponsorUsername || (tx as any).PromoSponsorUsername || ''),
        campaignName: String(tx.promoCampaignName || (tx as any).PromoCampaignName || ''),
        transactionType: 'GRANT',
        amount,
        balanceBefore,
        balanceAfter,
        description: String(
          tx.promoDescription || (tx as any).PromoDescription || tx.offerTitle || 'Vendor credit grant'
        ),
        transactionDate: String(tx.completedDate || tx.creditedDate || tx.createdDate),
        expirationDate: (tx.promoExpirationDate || (tx as any).PromoExpirationDate) ?? undefined,
        referenceType: 'PAYOUT_LINK',
        referenceId: tx.transactionId,
      });
      setPromoDetailsOpen(true);
      return;
    }

    if (!accountHistoryId) {
      setHistoryDetailError('Account History details are not available for this offer yet.');
      return;
    }
    setSelectedAccountTx({ transactionId: String(accountHistoryId) } as Transaction);
    setAccountDetailsOpen(true);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EarnIcon color="primary" />
          Earn Credits
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          variant="outlined"
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Summary Cards — Lifetime/Month/Pending/Reversed are Qix-only; vendor credits stay separate */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <Card elevation={2}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Lifetime Qix
            </Typography>
            <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
              {summaryLoading ? (
                <Skeleton width={80} />
              ) : (
                `$${(summary?.lifetimeEarnings ?? 0).toFixed(2)}`
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {summary?.totalCompletedOffers ?? 0} offers completed
            </Typography>
          </CardContent>
        </Card>

        <Card elevation={2}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              This Month (Qix)
            </Typography>
            <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
              {summaryLoading ? (
                <Skeleton width={80} />
              ) : (
                `$${(summary?.thisMonthEarnings ?? 0).toFixed(2)}`
              )}
            </Typography>
          </CardContent>
        </Card>

        <Card elevation={2}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Pending
            </Typography>
            <Typography variant="h6" color="warning.main" sx={{ fontWeight: 'bold' }}>
              {summaryLoading ? (
                <Skeleton width={80} />
              ) : (
                `$${(summary?.pendingAmount ?? 0).toFixed(2)}`
              )}
            </Typography>
          </CardContent>
        </Card>

        <Card elevation={2}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Reversed
            </Typography>
            <Typography variant="h6" color="error.main" sx={{ fontWeight: 'bold' }}>
              {summaryLoading ? (
                <Skeleton width={80} />
              ) : (
                `$${(summary?.reversedAmount ?? 0).toFixed(2)}`
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {summary?.totalReversedOffers ?? 0} reversed
            </Typography>
          </CardContent>
        </Card>

        <Card elevation={2}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Vendor credits
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'purple' }}>
              {summaryLoading ? (
                <Skeleton width={80} />
              ) : (
                `$${(summary?.vendorCreditsLifetime ?? 0).toFixed(2)}`
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {summary?.vendorCreditsOfferCount ?? 0} network claims
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Tabs — order: Qix Network, My Earnings, Earn; visibility from Admin Visible Tabs */}
      <Paper elevation={2}>
        {visibleSubTabs.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            No Earn Credits sections are visible for your account. An administrator can enable them under Visible Tabs.
          </Alert>
        ) : (
        <Tabs
          value={tabIndex}
          onChange={(_, newVal) => setTabIndex(newVal)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          {visibleSubTabs.map((t) => (
            <Tab key={t.key} icon={t.icon} iconPosition="start" label={t.label} />
          ))}
        </Tabs>
        )}

        {/* Earn (third-party offerwalls) */}
        {visibleSubTabs.some((t) => t.key === 'earn-credits/earn') && (
        <TabPanel value={tabIndex} index={visibleSubTabs.findIndex((t) => t.key === 'earn-credits/earn')}>
          <Box sx={{ px: 2, pb: 2 }}>
            {providersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : providersError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load providers. Please try again later.
              </Alert>
            ) : !providers || providers.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Earn Credits Coming Soon!
                </Typography>
                <Typography variant="body2">
                  Complete offers, surveys, and other activities to earn real 2Way Credits
                  that you can spend on anything in the marketplace. Providers are being
                  configured and will be available shortly.
                </Typography>
              </Alert>
            ) : (
              <>
                {providers.length > 1 && (
                  <FormControl size="small" sx={{ mb: 2, minWidth: 200 }}>
                    <InputLabel>Select Provider</InputLabel>
                    <Select
                      value={selectedProvider}
                      onChange={(e) => setSelectedProvider(e.target.value)}
                      label="Select Provider"
                    >
                      {providers.map((p) => (
                        <MenuItem key={p.providerCode} value={p.providerCode}>
                          {p.providerName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Complete offers below to earn real 2Way Credits. Credits are added to your
                    account balance immediately upon completion. Some offers may be reversed if
                    the advertiser declines the conversion.
                  </Typography>
                </Alert>

                {wallLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                  </Box>
                ) : wallUrl ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: '600px',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <iframe
                      src={wallUrl}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                      }}
                      title={`${selectedProvider} Offers`}
                      sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-top-navigation"
                    />
                  </Box>
                ) : (
                  <Alert severity="warning">
                    Unable to load the offer wall. Please try selecting a different provider
                    or refreshing the page.
                  </Alert>
                )}
              </>
            )}
          </Box>
        </TabPanel>
        )}

        {/* My Earnings (History) */}
        {visibleSubTabs.some((t) => t.key === 'earn-credits/my-earnings') && (
        <TabPanel value={tabIndex} index={visibleSubTabs.findIndex((t) => t.key === 'earn-credits/my-earnings')}>
          <Box sx={{ px: 2, pb: 2 }}>
            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : !historyData || historyData.transactions.length === 0 ? (
              <Alert severity="info">
                <Typography variant="body2">
                  You haven&apos;t completed any offers yet. Visit Qix Network or Earn to start earning credits!
                </Typography>
              </Alert>
            ) : (
              <>
                {historyDetailError && (
                  <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setHistoryDetailError('')}>
                    {historyDetailError}
                  </Alert>
                )}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Offer</TableCell>
                        <TableCell align="center">Type</TableCell>
                        <TableCell align="right">Earned</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {historyData.transactions.map((tx: EarnTransaction) => {
                        const isVendor = tx.paymentType === 1;
                        const isReversed = tx.status.toUpperCase() === 'REVERSED';
                        const amountColor = isReversed
                          ? 'error.main'
                          : isVendor
                            ? 'purple'
                            : 'success.main';
                        const canOpenDetail = Boolean(
                          (isVendor && (tx.promoHistoryId || (tx as any).PromoHistoryId))
                          || (!isVendor && (tx.accountHistoryId || (tx as any).AccountHistoryId))
                        );
                        return (
                        <TableRow
                          key={tx.transactionId}
                          hover
                          onClick={() => handleEarnRowClick(tx)}
                          sx={{
                            cursor: canOpenDetail ? 'pointer' : 'default',
                            '&:hover': canOpenDetail ? { bgcolor: 'action.hover' } : undefined,
                          }}
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatDate(tx.completedDate || tx.createdDate)}
                          </TableCell>
                          <TableCell>{tx.providerName}</TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 250,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {tx.offerTitle || 'Offer'}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={tx.paymentTypeLabel || (isVendor ? 'Vendor credits' : 'Qix')}
                              variant="outlined"
                              color={isVendor ? undefined : 'primary'}
                              sx={
                                isVendor
                                  ? {
                                      color: 'purple',
                                      borderColor: 'purple',
                                      '& .MuiChip-label': { color: 'purple' },
                                    }
                                  : undefined
                              }
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: amountColor,
                                fontWeight: 'bold',
                              }}
                            >
                              {isReversed ? '-' : '+'}$
                              {tx.amountEarned.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{getStatusChip(tx.status)}</TableCell>
                          <TableCell>
                            {isReversed && tx.reversalReason && (
                              <Typography variant="caption" color="error">
                                {tx.reversalReason}
                              </Typography>
                            )}
                            {tx.status.toUpperCase() === 'CREDITED' && tx.creditedDate && (
                              <Typography variant="caption" color="text.secondary">
                                Credited {formatDate(tx.creditedDate)}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={historyData.totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </>
            )}
          </Box>
        </TabPanel>
        )}

        {/* Qix Network offerwall */}
        {visibleSubTabs.some((t) => t.key === 'earn-credits/qix-network') && (
        <TabPanel value={tabIndex} index={visibleSubTabs.findIndex((t) => t.key === 'earn-credits/qix-network')}>
          <Box sx={{ px: 2, pb: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Browse in-house <strong>Qix Network</strong> ads (Qix and vendor-credit payouts).
                Third-party offerwalls remain on the Earn tab. Only offers you qualify for are shown.
                Click an ad to open PaymentFast in a new tab and claim credits.
              </Typography>
            </Alert>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ mb: 2, flexWrap: 'wrap' }}
              useFlexGap
            >
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Sort</InputLabel>
                <Select
                  label="Sort"
                  value={offerSort}
                  onChange={(e) => setOfferSort(e.target.value as AdOfferwallSort)}
                >
                  <MenuItem value="payDesc">Highest value</MenuItem>
                  <MenuItem value="payAsc">Lowest value</MenuItem>
                  <MenuItem value="category">Category</MenuItem>
                  <MenuItem value="newest">Newest</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={offerCategory}
                  onChange={(e) => setOfferCategory(e.target.value)}
                >
                  <MenuItem value="all">All categories</MenuItem>
                  {(adOffersData?.categories || []).map((c) => (
                    <MenuItem key={c.code} value={c.code}>{c.label || c.code}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Payout type</InputLabel>
                <Select
                  label="Payout type"
                  value={offerPaymentType}
                  onChange={(e) => setOfferPaymentType(e.target.value)}
                >
                  <MenuItem value="all">All types</MenuItem>
                  <MenuItem value="0">Qix</MenuItem>
                  <MenuItem value="1">Vendor credits</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Min value"
                value={offerMinPay}
                onChange={(e) => setOfferMinPay(e.target.value)}
                sx={{ width: 120 }}
                InputProps={{ startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>$</Typography> }}
              />
            </Stack>

            {offerOpenError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setOfferOpenError('')}>
                {offerOpenError}
              </Alert>
            )}

            {adOffersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : adOffersError ? (
              <Alert severity="error">
                Failed to load Qix Network offers. Please try again later.
              </Alert>
            ) : !adOffersData || adOffersData.offers.length === 0 ? (
              <Alert severity="info">
                No Qix Network offers available for your account right now.
              </Alert>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                  },
                  gap: 2,
                  alignItems: 'start',
                }}
              >
                {adOffersData.offers.map((offer) => (
                  <AdEarnImage
                    key={offer.creativeId}
                    src={offer.imageUrl}
                    alt={offer.name}
                    amount={offer.payAmount}
                    paymentType={offer.paymentType}
                    onClick={() => handleOpenOffer(offer.creativeId)}
                    disabled={openingCreativeId === offer.creativeId}
                    loading={openingCreativeId === offer.creativeId}
                    // Badge mark: stylized green 2Way arrow-"2" PNG. To restore Qix, set
                    // ACTIVE_OVERLAY_BADGE_LOGO = OVERLAY_BADGE_LOGO_QIX in shared/ad-network/overlayBadgeLogo.ts
                    logoSrc={ACTIVE_OVERLAY_BADGE_LOGO}
                    logoFallbackSrc={OVERLAY_BADGE_LOGO_2WAY_FALLBACK_SVG}
                  />
                ))}
              </Box>
            )}
          </Box>
        </TabPanel>
        )}
      </Paper>

      <TransactionDetailsDialog
        open={accountDetailsOpen}
        onClose={() => {
          setAccountDetailsOpen(false);
          setSelectedAccountTx(null);
        }}
        transaction={selectedAccountTx}
      />
      <PromoTransactionDetailsDialog
        open={promoDetailsOpen}
        onClose={() => {
          setPromoDetailsOpen(false);
          setSelectedPromoTx(null);
        }}
        transaction={selectedPromoTx}
      />
    </Container>
  );
};

export default EarnCredits;
